<script setup lang="ts">
import { Loader2, Plus, Search } from 'lucide-vue-next';
import { computed, onMounted, ref, watch } from 'vue';

import AzureIcon from '@/assets/azure.svg';
import MicrosoftConnectionNotice from '@/components/MicrosoftConnectionNotice.vue';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import AzureGroupMembersDialog from '@/components/user-groups/AzureGroupMembersDialog.vue';
import AzureGroupsDialog from '@/components/user-groups/AzureGroupsDialog.vue';
import CreateEditGroupDialog from '@/components/user-groups/CreateEditGroupDialog.vue';
import DeleteGroupDialog from '@/components/user-groups/DeleteGroupDialog.vue';
import UserGroupMembersDialog from '@/components/user-groups/UserGroupMembersDialog.vue';
import UserGroupsList from '@/components/user-groups/UserGroupsList.vue';
import {
    createUserGroup,
    deleteUserGroup,
    getUserGroups,
    updateUserGroup,
    type UserGroup
} from '@/services/user_groups';
import { useConfigCatStore } from '@/stores/configCat';
import { useTenantStore } from '@/stores/tenant';

const tenantStore = useTenantStore();
const configCatStore = useConfigCatStore();

const userGroups = ref<UserGroup[]>([]);
const loading = ref(false);
const error = ref<string | null>(null);
const editingGroup = ref<UserGroup | null>(null);
const isDeleteDialogOpen = ref(false);
const groupToDelete = ref<UserGroup | null>(null);
const pageReloading = ref(false);
const showCreateEditDialog = ref(false);
const searchQuery = ref('');

const showMembersDialog = ref(false);
const selectedGroupForMembers = ref<UserGroup | null>(null);

const showAzureMembersDialog = ref(false);
const selectedAzureGroupForMembers = ref<UserGroup | null>(null);

const isAzureGroupsDialogOpen = ref(false);

const formData = ref({
    name: '',
    description: ''
});

const tenantId = computed(() => tenantStore.tenantId);
const usesAzureGroups = computed(
    () =>
        configCatStore.azureGroups ||
        userGroups.value.some((group) => group.azure_group_id)
);

const filteredGroups = computed(() => {
    if (!searchQuery.value.trim()) {
        return userGroups.value;
    }

    const query = searchQuery.value.toLowerCase();
    return userGroups.value.filter((group) => {
        const nameMatch = group.name.toLowerCase().includes(query);
        const descriptionMatch = group.description
            ?.toLowerCase()
            .includes(query);
        return nameMatch || descriptionMatch;
    });
});

watch(tenantId, (newTenantId) => {
    if (newTenantId) {
        loadUserGroups();
    }
});

const loadUserGroups = async () => {
    if (!tenantId.value) return;

    loading.value = true;
    error.value = null;

    try {
        userGroups.value = await getUserGroups(tenantId.value);
    } catch (err) {
        console.error('Error loading user groups:', err);
        error.value = 'Failed to load user groups';
    } finally {
        loading.value = false;
    }
};

const resetForm = () => {
    formData.value = {
        name: '',
        description: ''
    };
    showCreateEditDialog.value = false;
    editingGroup.value = null;
};

const startCreate = () => {
    resetForm();
    showCreateEditDialog.value = true;
};

const startEdit = (group: UserGroup) => {
    editingGroup.value = group;
    showCreateEditDialog.value = true;
};

const handleCreateEditSubmit = async (data: {
    name: string;
    description?: string;
}) => {
    if (!tenantId.value) return;

    pageReloading.value = true;
    error.value = null;

    try {
        if (editingGroup.value) {
            await updateUserGroup(editingGroup.value.id, tenantId.value, data);
        } else {
            await createUserGroup({
                ...data,
                tenant_id: tenantId.value
            });
        }

        resetForm();
        await loadUserGroups();
    } catch (err) {
        error.value = editingGroup.value
            ? 'Failed to update group'
            : 'Failed to create group';
        console.error('Error saving group:', err);
    } finally {
        pageReloading.value = false;
    }
};

const handleOpenDeleteDialog = (group: UserGroup) => {
    groupToDelete.value = group;
    isDeleteDialogOpen.value = true;
};

const handleDeleteConfirm = async () => {
    if (!tenantId.value || !groupToDelete.value) return;

    try {
        isDeleteDialogOpen.value = false;
        pageReloading.value = true;
        await deleteUserGroup(groupToDelete.value.id, tenantId.value);
        await loadUserGroups();
        groupToDelete.value = null;
    } catch (err) {
        error.value = 'Failed to delete group';
        console.error('Error deleting group:', err);
    } finally {
        pageReloading.value = false;
    }
};

const handleManageMembers = (group: UserGroup) => {
    selectedGroupForMembers.value = group;
    showMembersDialog.value = true;
};

const handleViewMembers = (group: UserGroup) => {
    selectedAzureGroupForMembers.value = group;
    showAzureMembersDialog.value = true;
};

const handleViewAzureGroups = () => {
    isAzureGroupsDialogOpen.value = true;
};

const handleGroupImported = async () => {
    await loadUserGroups();
};

const handleMemberAdded = (data: { groupId: number; memberCount: number }) => {
    const groupIndex = userGroups.value.findIndex(
        (group) => group.id === data.groupId
    );
    if (groupIndex !== -1) {
        userGroups.value[groupIndex].member_count = data.memberCount;
    }
};

const handleMemberRemoved = (data: {
    groupId: number;
    memberCount: number;
}) => {
    const groupIndex = userGroups.value.findIndex(
        (group) => group.id === data.groupId
    );
    if (groupIndex !== -1) {
        userGroups.value[groupIndex].member_count = data.memberCount;
    }
};

onMounted(() => {
    loadUserGroups();
});
</script>

<template>
    <DeleteGroupDialog
        v-model:open="isDeleteDialogOpen"
        :group="groupToDelete"
        @confirm="handleDeleteConfirm"
    />

    <CreateEditGroupDialog
        v-model:open="showCreateEditDialog"
        v-model:form-data="formData"
        :editing-group="editingGroup"
        :user-groups="userGroups"
        :error="error"
        :loading="pageReloading"
        @submit="handleCreateEditSubmit"
    />

    <UserGroupMembersDialog
        v-model:open="showMembersDialog"
        :group="selectedGroupForMembers"
        :tenant-id="tenantId || ''"
        @member-added="handleMemberAdded"
        @member-removed="handleMemberRemoved"
    />

    <AzureGroupMembersDialog
        v-model:open="showAzureMembersDialog"
        :group="selectedAzureGroupForMembers"
        :tenant-id="tenantId || ''"
    />

    <AzureGroupsDialog
        v-model:open="isAzureGroupsDialogOpen"
        :tenant-id="tenantId || ''"
        :user-groups="userGroups"
        @group-imported="handleGroupImported"
    />

    <div
        v-if="loading || pageReloading"
        class="absolute inset-0 z-20 flex min-h-full w-full items-center justify-center bg-primary30 opacity-40"
    >
        <Loader2 class="h-8 w-8 animate-spin text-foreground" />
    </div>

    <div v-else class="space-y-6">
        <MicrosoftConnectionNotice
            v-if="usesAzureGroups"
            feature="azure_groups"
        />

        <div
            v-if="error"
            class="rounded-lg border border-destructive/20 bg-destructive/10 p-4"
        >
            <p class="text-destructive">{{ error }}</p>
        </div>

        <div
            v-if="userGroups.length > 0"
            class="flex flex-col items-start gap-3 sm:flex-row sm:items-center"
        >
            <Button @click="startCreate" :disabled="pageReloading">
                <Plus class="mr-2 h-4 w-4" />
                Create New Group
            </Button>
            <Button
                v-if="configCatStore.azureGroups"
                @click="handleViewAzureGroups"
                :disabled="pageReloading"
                variant="outline"
            >
                <img :src="AzureIcon" class="mr-2 h-4 w-4" alt="Azure" />
                Import from Azure AD Groups
            </Button>
        </div>

        <div v-if="userGroups.length > 0" class="relative w-full">
            <Search
                class="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            />
            <Input
                v-model="searchQuery"
                type="text"
                placeholder="Search groups by name or description..."
                class="pl-9"
            />
        </div>

        <div
            v-if="userGroups.length > 0 && filteredGroups.length === 0"
            class="flex h-64 items-center justify-center"
        >
            <div class="text-center">
                <Search class="mx-auto mb-2 h-12 w-12 text-muted-foreground" />
                <p class="text-sm text-muted-foreground">
                    No groups match your search
                </p>
            </div>
        </div>

        <UserGroupsList
            v-else
            :groups="filteredGroups"
            :disabled="pageReloading"
            @create-group="startCreate"
            @edit-group="startEdit"
            @delete-group="handleOpenDeleteDialog"
            @manage-members="handleManageMembers"
            @view-members="handleViewMembers"
            @view-azure-groups="handleViewAzureGroups"
        />
    </div>
</template>
